<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import FileUpload from 'vue-upload-component';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import {
  MAXIMUM_FILE_UPLOAD_SIZE,
  ALLOWED_FILE_TYPES,
} from 'shared/constants/messages';
import { emitter } from 'shared/helpers/mitt';
import Modal from 'dashboard/components/Modal.vue';
import SendMessageTemplate from './SendMessageTemplate.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import KanbanAPI from 'dashboard/api/kanban';
import ContactAPI from 'dashboard/api/contacts';
import conversationAPI from 'dashboard/api/inbox/conversation';
import MarkdownIt from 'markdown-it';

// Props
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  notes: {
    type: Array,
    required: true,
  },
  isStacklab: {
    type: Boolean,
    default: false,
  },
  kanbanItems: {
    type: Array,
    default: () => [],
  },
  conversations: {
    type: Array,
    default: () => [],
  },
  contactsList: {
    type: Array,
    default: () => [],
  },
  loadingItems: {
    type: Boolean,
    default: false,
  },
  loadingConversations: {
    type: Boolean,
    default: false,
  },
  loadingContacts: {
    type: Boolean,
    default: false,
  },
  currentUser: {
    type: Object,
    required: true,
  },
});

// Emits
const emit = defineEmits([
  'update:item',
  'item-updated',
  'upload-attachment',
  'delete-attachment',
  'selection-change',
]);

const { t } = useI18n();

// Instância do MarkdownIt com configurações padrão
const md = new MarkdownIt({
  html: false, // Desabilitar HTML inline para segurança
  breaks: true, // Converter quebras de linha em <br>
  linkify: true, // Converter URLs em links automaticamente
});

// Função para renderizar markdown
const renderMarkdown = text => {
  if (!text) return '';

  // Renderizar diretamente - markdown-it já suporta listas por padrão
  return md.render(text);
};

// Refs para o componente
const currentNote = ref('');
const savingNotes = ref(false);
const editingNoteId = ref(null);
const editingNoteContent = ref('');
const noteAttachments = ref([]);
const isUploadingAttachment = ref(false);
const selectedFileName = ref('');
const loadingNotes = ref(false);
const internalNotes = ref([]);

// Refs para dropdown de vinculação
const showLinkDropdown = ref(false);

// Refs para vinculação
const showItemSelector = ref(false);
const showConversationSelector = ref(false);
const showContactSelector = ref(false);
const fetchedConversationsList = ref(null);
const fetchingConversationsModal = ref(false);
const fetchedContactsList = ref(null);
const fetchingContactsModal = ref(false);
const selectedItemId = ref(null);
const selectedConversationId = ref(null);
const selectedContactId = ref(null);

// Chave de armazenamento local para o rascunho da nota
const getNoteDraftKey = () => {
  if (props.item?.id) {
    return `kanban_note_draft_${props.item.id}`;
  }
  return 'kanban_note_draft';
};

// Computed properties
const selectedLinksPreview = computed(() => {
  const links = [];
  if (selectedItemId.value) {
    links.push({ type: 'item', id: selectedItemId.value });
  }
  if (selectedConversationId.value) {
    links.push({ type: 'conversation', id: selectedConversationId.value });
  }
  if (selectedContactId.value) {
    links.push({ type: 'contact', id: selectedContactId.value });
  }
  return links;
});

const resolvedContactsList = computed(() =>
  fetchedContactsList.value !== null
    ? fetchedContactsList.value
    : props.contactsList
);

const contactsLoadingForModal = computed(
  () =>
    fetchingContactsModal.value ||
    (props.loadingContacts && fetchedContactsList.value === null)
);

const loadContactsForLinkModal = async () => {
  try {
    fetchingContactsModal.value = true;
    const response = await ContactAPI.get(1);
    fetchedContactsList.value = response.data?.payload ?? [];
  } catch (error) {
    console.error('Erro ao carregar contatos:', error);
    fetchedContactsList.value = [];
  } finally {
    fetchingContactsModal.value = false;
  }
};

watch(showContactSelector, open => {
  if (open) {
    loadContactsForLinkModal();
  }
});

const resolvedConversationsList = computed(() =>
  fetchedConversationsList.value !== null
    ? fetchedConversationsList.value
    : props.conversations
);

const conversationsLoadingForModal = computed(
  () =>
    fetchingConversationsModal.value ||
    (props.loadingConversations && fetchedConversationsList.value === null)
);

const loadConversationsForLinkModal = async () => {
  try {
    fetchingConversationsModal.value = true;
    const response = await conversationAPI.get({});
    const payload = response.data?.data?.payload ?? [];
    fetchedConversationsList.value = payload.map(c => ({
      id: c.id,
      title:
        c.meta?.sender?.name ||
        c.meta?.sender?.email ||
        t('KANBAN.ITEM_CONVERSATION.NO_CONTACT'),
      description: c.messages?.[0]?.content || t('KANBAN.NO_MESSAGES'),
      created_at: new Date(c.created_at).toLocaleDateString(),
      channel_type: c.channel_type,
      status: c.status,
    }));
  } catch (error) {
    console.error('Erro ao carregar conversas:', error);
    fetchedConversationsList.value = [];
  } finally {
    fetchingConversationsModal.value = false;
  }
};

watch(showConversationSelector, open => {
  if (open) {
    loadConversationsForLinkModal();
  }
});

const itemButtonText = computed(() => {
  if (showItemSelector.value) {
    return t('KANBAN.CANCEL');
  }

  if (selectedItemId.value) {
    const selectedItem = props.kanbanItems.find(
      item => item.id === selectedItemId.value
    );
    return selectedItem?.title || t('KANBAN.FORM.NOTES.LINK_ITEM');
  }

  return t('KANBAN.FORM.NOTES.LINK_ITEM');
});

const conversationButtonText = computed(() => {
  if (showConversationSelector.value) {
    return t('KANBAN.CANCEL');
  }

  if (selectedConversationId.value) {
    const selectedConversation = resolvedConversationsList.value.find(
      conv => conv.id === selectedConversationId.value
    );
    return selectedConversation
      ? `#${selectedConversation.id} - ${selectedConversation.title}`
      : t('KANBAN.FORM.NOTES.LINK_CONVERSATION');
  }

  return t('KANBAN.FORM.NOTES.LINK_CONVERSATION');
});

const contactButtonText = computed(() => {
  if (showContactSelector.value) {
    return t('KANBAN.CANCEL');
  }

  if (selectedContactId.value) {
    const selectedContact = resolvedContactsList.value.find(
      contact => contact.id === selectedContactId.value
    );
    return selectedContact?.name || t('KANBAN.FORM.NOTES.LINK_CONTACT');
  }

  return t('KANBAN.FORM.NOTES.LINK_CONTACT');
});

// Funções utilitárias
const isImage = attachment => {
  try {
    return !!(
      attachment &&
      typeof attachment === 'object' &&
      attachment.fileType &&
      typeof attachment.fileType === 'string' &&
      attachment.fileType.startsWith('image/')
    );
  } catch (error) {
    return false;
  }
};

const hasNonImageAttachments = note => {
  return note.attachments?.some(a => !isImage(a)) || false;
};

const hasImageAttachments = note => {
  return note.attachments?.some(isImage) || false;
};

const getNonImageAttachments = note => {
  return note.attachments?.filter(a => !isImage(a)) || [];
};

const getImageAttachments = note => {
  return note.attachments?.filter(isImage) || [];
};

const getFileIcon = attachment => {
  if (!attachment || !attachment.fileType) return '📎';
  return attachment.fileType.includes('pdf') ? '📄' : '📎';
};

const formatDate = date => {
  if (!date) return '';
  try {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return '';
  }
};

// Funções para manipulação de notas
const handleAddNote = async () => {
  if (
    !currentNote.value.trim() &&
    !noteAttachments.value?.length &&
    !selectedItemId.value &&
    !selectedConversationId.value &&
    !selectedContactId.value
  )
    return;

  try {
    const noteData = {
      text: currentNote.value,
      attachments: noteAttachments.value,
      linked_item_id: selectedItemId.value,
      linked_conversation_id: selectedConversationId.value,
      linked_contact_id: selectedContactId.value,
    };

    // Se está editando, atualizar a nota existente
    if (editingNoteId.value) {
      await KanbanAPI.updateNote(props.item.id, editingNoteId.value, noteData);

      emitter.emit('newToastMessage', {
        message: t('KANBAN.NOTES.UPDATED_SUCCESSFULLY'),
        action: { type: 'success' },
      });
    } else {
      // Caso contrário, criar nova nota
      await KanbanAPI.createNote(props.item.id, noteData);

      emitter.emit('newToastMessage', {
        message: t('KANBAN.NOTES.CREATED_SUCCESSFULLY'),
        action: { type: 'success' },
      });
    }

    // Buscar notas atualizadas
    await fetchNotes();

    // Limpar estado
    currentNote.value = '';
    noteAttachments.value = [];
    selectedFileName.value = '';
    selectedItemId.value = null;
    selectedConversationId.value = null;
    selectedContactId.value = null;
    editingNoteId.value = null;

    // Limpar rascunho salvo
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(getNoteDraftKey());
    }
  } catch (error) {
    console.error('Erro ao salvar nota:', error);
    emitter.emit('newToastMessage', {
      message: editingNoteId.value
        ? t('KANBAN.NOTES.UPDATE_ERROR')
        : t('KANBAN.NOTES.CREATE_ERROR'),
      action: { type: 'error' },
    });
  }
};

const handleEditNote = note => {
  editingNoteId.value = note.id;
  currentNote.value = note.text;
  selectedItemId.value = note.linked_item_id || null;
  selectedConversationId.value = note.linked_conversation_id || null;
  selectedContactId.value = note.linked_contact_id || null;
  noteAttachments.value = note.attachments || [];
};

const itemConversation = computed(
  () =>
    props.item?.conversation || props.item?.item_details?.conversation || null
);

const showSendMessageModal = ref(false);
const conversationForSendMessage = ref(null);
const initialNoteSendMessageText = ref('');
const sendMessageModalKey = ref(0);

const resolveConversationForNote = async note => {
  if (note.linked_conversation_id) {
    try {
      const response = await conversationAPI.get({});
      const list = response.data?.data?.payload ?? [];
      const found = list.find(c => c.id === note.linked_conversation_id);
      if (found) return found;
    } catch (error) {
      console.error('Erro ao resolver conversa da nota:', error);
    }
  }
  return itemConversation.value;
};

const closeSendMessageModal = () => {
  showSendMessageModal.value = false;
  conversationForSendMessage.value = null;
  initialNoteSendMessageText.value = '';
};

const handleSendNoteAsMessage = async note => {
  const conversation = await resolveConversationForNote(note);
  if (!conversation?.display_id) {
    emitter.emit('newToastMessage', {
      message: t('KANBAN.NOTES.NO_CONVERSATION_FOR_SEND'),
      action: { type: 'error' },
    });
    return;
  }
  conversationForSendMessage.value = conversation;
  initialNoteSendMessageText.value = note.text || '';
  sendMessageModalKey.value += 1;
  showSendMessageModal.value = true;
};

const sendMessageModalContact = computed(() => {
  const c = conversationForSendMessage.value;
  if (!c) return {};
  return c.contact || c.meta?.sender || {};
});

const handleDeleteNote = async noteId => {
  // SiteUp (#83): confirma antes de excluir — evita perda acidental de nota.
  if (!window.confirm('Excluir esta nota permanentemente?')) return;
  try {
    await KanbanAPI.deleteNote(props.item.id, noteId);

    // Buscar notas atualizadas
    await fetchNotes();

    // Mostrar mensagem de sucesso
    emitter.emit('newToastMessage', {
      message: t('KANBAN.NOTES.DELETED_SUCCESSFULLY'),
      action: { type: 'success' },
    });
  } catch (error) {
    console.error('Erro ao deletar nota:', error);
    emitter.emit('newToastMessage', {
      message: t('KANBAN.NOTES.DELETE_ERROR'),
      action: { type: 'error' },
    });
  }
};

// Função para fechar dropdown após upload de arquivo
const closeDropdownAfterUpload = () => {
  showLinkDropdown.value = false;
};

const cancelEditNote = () => {
  editingNoteId.value = null;
  currentNote.value = '';
  noteAttachments.value = [];
  selectedFileName.value = '';
  selectedItemId.value = null;
  selectedConversationId.value = null;
  selectedContactId.value = null;
  showItemSelector.value = false;
  showConversationSelector.value = false;
  showContactSelector.value = false;
  showLinkDropdown.value = false;

  // Limpar rascunho salvo ao cancelar
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(getNoteDraftKey());
  }
};

// Salvar rascunho da nota no localStorage sempre que o conteúdo mudar
watch(currentNote, newValue => {
  if (typeof window === 'undefined') return;

  const key = getNoteDraftKey();
  const value = newValue || '';

  if (value.trim()) {
    window.localStorage.setItem(key, value);
  } else {
    window.localStorage.removeItem(key);
  }
});

// Funções para upload de anexos
const handleNoteAttachment = async file => {
  if (!file) return;

  selectedFileName.value = file.file.name;
  closeDropdownAfterUpload(); // Fecha o dropdown quando iniciar upload

  if (!props.item?.id) {
    console.error('ID do item não encontrado:', props.item);
    return;
  }

  isUploadingAttachment.value = true;
  try {
    if (checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE)) {
      emit('upload-attachment', {
        file,
        type: 'note',
        source: editingNoteId.value
          ? { type: 'note', id: editingNoteId.value }
          : null,
      });
    }
  } catch (error) {
    console.error('Erro ao fazer upload:', error.message);
  } finally {
    isUploadingAttachment.value = false;
  }
};

// Funções para dropdown de vinculação
const toggleLinkDropdown = () => {
  showLinkDropdown.value = !showLinkDropdown.value;
};

const selectLinkType = type => {
  showLinkDropdown.value = false;

  switch (type) {
    case 'item':
      toggleItemSelector();
      break;
    case 'conversation':
      toggleConversationSelector();
      break;
    case 'contact':
      toggleContactSelector();
      break;
  }
};

// Funções para seletores
const toggleItemSelector = () => {
  if (showItemSelector.value) {
    showItemSelector.value = false;
  } else {
    showItemSelector.value = true;
    showConversationSelector.value = false;
    showContactSelector.value = false;
  }
};

const toggleConversationSelector = () => {
  if (showConversationSelector.value) {
    showConversationSelector.value = false;
  } else {
    showConversationSelector.value = true;
    showItemSelector.value = false;
    showContactSelector.value = false;
  }
};

const toggleContactSelector = () => {
  if (showContactSelector.value) {
    showContactSelector.value = false;
  } else {
    showContactSelector.value = true;
    showItemSelector.value = false;
    showConversationSelector.value = false;
  }
};

// Funções para seleção de itens
const selectItem = item => {
  selectedItemId.value = item.id;
  showItemSelector.value = false;
};

const selectConversation = conversation => {
  selectedConversationId.value = conversation.id;
  showConversationSelector.value = false;
};

const selectContact = contact => {
  selectedContactId.value = contact.id;
  showContactSelector.value = false;
};

// Computed para filtrar listas
const filteredKanbanItems = computed(() => {
  if (!itemSearchTerm.value) return props.kanbanItems;
  return props.kanbanItems.filter(item =>
    (item.title || '')
      .toLowerCase()
      .includes(itemSearchTerm.value.toLowerCase())
  );
});

const filteredConversations = computed(() => {
  const list = resolvedConversationsList.value;
  if (!conversationSearchTerm.value) return list;
  return list.filter(conv =>
    ((conv.title || '') + ' ' + (conv.description || ''))
      .toLowerCase()
      .includes(conversationSearchTerm.value.toLowerCase())
  );
});

const filteredContactsList = computed(() => {
  const list = resolvedContactsList.value;
  if (!contactSearchTerm.value) return list;
  return list.filter(contact =>
    (contact.name || '')
      .toLowerCase()
      .includes(contactSearchTerm.value.toLowerCase())
  );
});

// Refs para busca
const itemSearchTerm = ref('');
const conversationSearchTerm = ref('');
const contactSearchTerm = ref('');

// Ref para controle de ordenação
const sortOrder = ref('newest'); // 'newest', 'oldest'

// Refs para seleção de notas
const selectedNoteIds = ref(new Set());

// Computed para usar notas internas ou da prop
const displayNotes = computed(() => {
  // Priorizar notas internas, depois props.notes, garantir sempre um array
  let notes =
    Array.isArray(internalNotes.value) && internalNotes.value.length > 0
      ? internalNotes.value
      : Array.isArray(props.notes)
        ? props.notes
        : [];

  if (!notes || notes.length === 0) return [];

  // Ordenar notas baseado no critério selecionado
  return [...notes].sort((a, b) => {
    switch (sortOrder.value) {
      case 'oldest':
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      case 'newest':
      default:
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    }
  });
});

// Função para obter detalhes de item vinculado
const getLinkedItemDetails = itemId => {
  return props.kanbanItems.find(item => item.id === itemId);
};

const getLinkedConversationLabel = conversationId => {
  if (!conversationId) return '';
  const c = resolvedConversationsList.value.find(
    conv => conv.id === conversationId
  );
  return c ? `#${c.id} - ${c.title}` : `#${conversationId}`;
};

// Função para remover anexo
const removeAttachment = attachment => {
  emit('delete-attachment', attachment);
};

// Função para buscar notas do item
const fetchNotes = async () => {
  try {
    loadingNotes.value = true;
    const { data } = await KanbanAPI.getNotes(props.item.id);
    internalNotes.value = Array.isArray(data.notes) ? data.notes : [];
  } catch (error) {
    console.error('Erro ao buscar notas:', error);
    emitter.emit('newToastMessage', {
      message: t('KANBAN.NOTES.FETCH_ERROR'),
      action: { type: 'error' },
    });
  } finally {
    loadingNotes.value = false;
  }
};

// Carregar rascunho e notas quando o componente for montado
onMounted(() => {
  if (typeof window !== 'undefined') {
    const key = getNoteDraftKey();
    const savedDraft = window.localStorage.getItem(key);
    if (savedDraft) {
      currentNote.value = savedDraft;
    }
  }

  if (props.item?.id) {
    fetchNotes();
  }

  syncLinkedItemCardDark();
  if (typeof document !== 'undefined') {
    linkedItemCardDarkObserver = new MutationObserver(syncLinkedItemCardDark);
    linkedItemCardDarkObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }
});

onUnmounted(() => {
  linkedItemCardDarkObserver?.disconnect();
  linkedItemCardDarkObserver = null;
});

// Função para alternar ordenação
const toggleSortOrder = () => {
  const sortOptions = ['newest', 'oldest'];
  const currentIndex = sortOptions.indexOf(sortOrder.value);
  const nextIndex = (currentIndex + 1) % sortOptions.length;
  sortOrder.value = sortOptions[nextIndex];
};

// Função para obter o texto da ordenação atual
const getSortOrderText = () => {
  switch (sortOrder.value) {
    case 'newest':
      return t('KANBAN.NOTES.SORT_NEWEST');
    case 'oldest':
      return t('KANBAN.NOTES.SORT_OLDEST');
    default:
      return t('KANBAN.NOTES.SORT_NEWEST');
  }
};

// Função para abrir preview de imagem
const openImagePreview = url => {
  window.open(url, '_blank');
};

const isNoteSelected = noteId => selectedNoteIds.value.has(noteId);

const toggleNoteSelection = (e, noteId) => {
  e.stopPropagation();
  const next = new Set(selectedNoteIds.value);
  if (next.has(noteId)) next.delete(noteId);
  else next.add(noteId);
  selectedNoteIds.value = next;
  emit('selection-change', Array.from(next));
};

const hasSelection = computed(() => selectedNoteIds.value.size > 0);

const selectAllNotes = () => {
  const allIds = displayNotes.value.map(n => n.id);
  selectedNoteIds.value = new Set(allIds);
  emit('selection-change', allIds);
};

const isDark = ref(false);
let linkedItemCardDarkObserver = null;

const syncLinkedItemCardDark = () => {
  isDark.value =
    typeof document !== 'undefined' && document.body.classList.contains('dark');
};

const linkedItemNoteCardStyle = computed(() =>
  isDark.value
    ? {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px',
        borderRadius: '8px',
        backgroundColor: 'rgba(30, 58, 138, 0.2)',
        border: '1px solid #1e40af',
      }
    : {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px',
        borderRadius: '8px',
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
      }
);

const linkedItemNoteIconColor = computed(() =>
  isDark.value ? '#60a5fa' : '#2563eb'
);

const linkedItemNoteTextColor = computed(() =>
  isDark.value ? '#93c5fd' : '#1d4ed8'
);

const linkedItemPreviewRowStyle = computed(() => ({
  ...linkedItemNoteCardStyle.value,
  justifyContent: 'space-between',
}));

const linkedItemNoteDismissColor = computed(() =>
  isDark.value ? '#93c5fd' : '#60a5fa'
);

const handleDeleteSelected = async () => {
  const ids = Array.from(selectedNoteIds.value);
  if (!ids.length) return;
  // SiteUp (#83): confirma a exclusão em massa, mostrando a contagem.
  if (
    !window.confirm(
      `Excluir ${ids.length} nota(s) selecionada(s) permanentemente?`
    )
  )
    return;
  try {
    for (const id of ids) {
      await KanbanAPI.deleteNote(props.item.id, id);
    }
    await fetchNotes();
    selectedNoteIds.value = new Set();
    emit('selection-change', []);
    emitter.emit('newToastMessage', {
      message: t('KANBAN.NOTES.DELETED_SUCCESSFULLY'),
      action: { type: 'success' },
    });
  } catch (e) {
    console.error(e);
    await fetchNotes();
    selectedNoteIds.value = new Set();
    emit('selection-change', []);
    emitter.emit('newToastMessage', {
      message: t('KANBAN.NOTES.DELETE_ERROR'),
      action: { type: 'error' },
    });
  }
};
</script>

<template>
  <div class="space-y-4">
    <!-- Campo de texto para nova nota -->
    <div class="note-input-section">
      <Editor
        v-model="currentNote"
        :placeholder="t('KANBAN.FORM.NOTES.PLACEHOLDER')"
        :max-length="2000"
        :show-character-count="true"
        :enable-variables="false"
        :enable-canned-responses="false"
        :enable-captain-tools="false"
        :enabled-menu-options="[]"
      />

      <!-- Botões de ação -->
      <div class="flex items-center justify-between mt-2">
        <!-- Botão único de vinculação com dropdown -->
        <div v-if="isStacklab" class="relative">
          <!-- Botão principal de vinculação -->
          <button
            type="button"
            class="action-button action-button-labeled hover:bg-n-alpha-2"
            :class="{ 'bg-n-alpha-2': showLinkDropdown }"
            @click="toggleLinkDropdown"
          >
            <fluent-icon
              :icon="showLinkDropdown ? 'dismiss' : 'attach'"
              size="20"
              class="shrink-0"
            />
            <span class="text-sm font-medium text-n-slate-12 whitespace-nowrap">
              {{
                showLinkDropdown
                  ? t('KANBAN.CANCEL')
                  : t('KANBAN.FORM.NOTES.ATTACH_LINK_LABEL')
              }}
            </span>
          </button>

          <!-- Dropdown de opções -->
          <div
            v-if="showLinkDropdown"
            class="link-dropdown absolute top-full mt-2 left-0 bg-n-solid-2 border border-n-weak rounded-xl shadow-lg py-2 min-w-[180px] flex flex-col items-start"
          >
            <!-- Opção de upload de arquivo -->
            <FileUpload
              ref="upload"
              :accept="ALLOWED_FILE_TYPES"
              @input-file="handleNoteAttachment"
            >
              <button
                class="link-dropdown-item"
                :disabled="isUploadingAttachment"
              >
                <span class="i-ri-attachment-line size-4 text-n-slate-11" />
                <span>{{
                  isUploadingAttachment
                    ? '...'
                    : t('KANBAN.FORM.NOTES.ATTACH_FILE')
                }}</span>
              </button>
            </FileUpload>

            <!-- Separador -->
            <div class="border-t border-n-weak my-1" />

            <!-- Opções de vinculação -->
            <button class="link-dropdown-item" @click="selectLinkType('item')">
              <span class="i-ri-link size-4 text-blue-600 dark:text-blue-400" />
              <span>{{ t('KANBAN.FORM.NOTES.LINK_ITEM') }}</span>
            </button>
            <button
              class="link-dropdown-item"
              @click="selectLinkType('conversation')"
            >
              <span class="i-ri-chat-1-line size-4 text-n-slate-11" />
              <span>{{ t('KANBAN.FORM.NOTES.LINK_CONVERSATION') }}</span>
            </button>
            <button
              class="link-dropdown-item"
              @click="selectLinkType('contact')"
            >
              <span
                class="i-ri-user-line size-4 text-purple-600 dark:text-purple-400"
              />
              <span>{{ t('KANBAN.FORM.NOTES.LINK_CONTACT') }}</span>
            </button>
          </div>
        </div>

        <!-- Botão de adicionar nota -->
        <button
          class="primary-button"
          :disabled="
            !currentNote.trim() &&
            !noteAttachments.length &&
            !selectedItemId &&
            !selectedConversationId &&
            !selectedContactId
          "
          @click="handleAddNote"
        >
          {{ editingNoteId ? t('SAVE') : t('KANBAN.ADD') }}
        </button>
      </div>

      <!-- Preview do arquivo selecionado -->
      <div
        v-if="selectedFileName"
        class="flex items-center gap-2 px-2 py-1 text-xs text-n-slate-11 mt-2"
      >
        <span class="i-ri-file-line size-3" />
        <span class="truncate">{{ selectedFileName }}</span>
      </div>

      <!-- Preview das seleções ativas -->
      <div
        v-if="selectedItemId || selectedConversationId || selectedContactId"
        class="selected-links-preview mt-2 mb-3 space-y-2"
      >
        <!-- Item selecionado -->
        <div v-if="selectedItemId" class="selected-link">
          <div :style="linkedItemPreviewRowStyle">
            <div class="flex items-center gap-2 min-w-0 flex-1">
              <span
                class="inline-flex shrink-0"
                :style="{ color: linkedItemNoteIconColor }"
              >
                <span class="i-ri-link size-3.5" />
              </span>
              <span
                class="text-sm truncate"
                :style="{ color: linkedItemNoteTextColor }"
              >
                {{
                  getLinkedItemDetails(selectedItemId)?.title ||
                  `Item #${selectedItemId}`
                }}
              </span>
            </div>
            <button
              type="button"
              class="p-1 shrink-0"
              :style="{ color: linkedItemNoteDismissColor }"
              @click="selectedItemId = null"
            >
              <span class="i-ri-close-line size-3" />
            </button>
          </div>
        </div>

        <!-- Conversa selecionada -->
        <div v-if="selectedConversationId" class="selected-link">
          <div
            class="flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800"
          >
            <div class="flex items-center gap-2">
              <span
                class="i-ri-chat-1-line size-3.5 text-green-600 dark:text-green-400"
              />
              <span
                class="text-sm text-green-700 dark:text-green-300 truncate min-w-0"
              >
                {{ getLinkedConversationLabel(selectedConversationId) }}
              </span>
            </div>
            <button
              class="p-1 text-green-400 hover:text-green-600 dark:hover:text-green-300"
              @click="selectedConversationId = null"
            >
              <span class="i-ri-close-line size-3" />
            </button>
          </div>
        </div>

        <!-- Contato selecionado -->
        <div v-if="selectedContactId" class="selected-link">
          <div
            class="flex items-center justify-between p-2 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800"
          >
            <div class="flex items-center gap-2">
              <span
                class="i-ri-user-line size-3.5 text-purple-600 dark:text-purple-400"
              />
              <span class="text-sm text-purple-700 dark:text-purple-300">
                {{
                  resolvedContactsList.find(c => c.id === selectedContactId)
                    ?.name || `Contato #${selectedContactId}`
                }}
              </span>
            </div>
            <button
              class="p-1 text-purple-400 hover:text-purple-600 dark:hover:text-purple-300"
              @click="selectedContactId = null"
            >
              <span class="i-ri-close-line size-3" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Seletor de itens -->
    <Modal
      v-if="showItemSelector"
      :show="showItemSelector"
      @close="showItemSelector = false"
    >
      <div class="item-selector p-0 bg-transparent border-none shadow-none">
        <div class="px-4 py-3 border-b border-n-weak">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('KANBAN.FORM.NOTES.SELECT_ITEM') }}
          </h4>
        </div>
        <!-- Caixa de busca -->
        <div class="px-4 py-2">
          <input
            v-model="itemSearchTerm"
            type="text"
            class="w-full px-3 py-2 text-sm border border-n-weak rounded-xl focus:ring-1 focus:ring-woot-500 focus:border-woot-500 bg-n-solid-2"
            placeholder="Buscar..."
          />
        </div>
        <div class="max-h-[280px] overflow-y-auto custom-scrollbar">
          <div
            v-if="loadingItems"
            class="p-4 text-center text-sm text-n-slate-11"
          >
            <span class="loading-spinner w-4 h-4 mr-2" />
            {{ t('KANBAN.LOADING') }}
          </div>
          <div
            v-else-if="filteredKanbanItems.length === 0"
            class="p-4 text-center"
          >
            <p class="text-sm text-n-slate-11">
              {{ t('KANBAN.FORM.NOTES.NO_ITEMS') }}
            </p>
          </div>
          <div v-else class="divide-y divide-n-weak">
            <button
              v-for="item in filteredKanbanItems"
              :key="item.id"
              class="w-full px-4 py-3 text-left hover:bg-n-alpha-2 transition-colors focus:outline-none focus:bg-n-alpha-1"
              @click="selectItem(item)"
            >
              <div class="flex items-center gap-3">
                <!-- Prioridade -->
                <div
                  class="flex-shrink-0 w-1.5 h-5 rounded-full"
                  :class="{
                    'bg-n-ruby-9': item.priority === 'high',
                    'bg-yellow-500': item.priority === 'medium',
                    'bg-green-500': item.priority === 'low',
                    'bg-n-alpha-3': item.priority === 'none',
                  }"
                />
                <div class="flex items-center gap-3 flex-1 min-w-0">
                  <h4
                    class="text-sm font-medium text-n-slate-12 truncate flex-1"
                  >
                    {{ item.title }}
                  </h4>
                  <span
                    class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11 whitespace-nowrap"
                  >
                    {{ item.stage_name }}
                  </span>
                  <span
                    class="flex items-center gap-1 text-xs text-n-slate-10 whitespace-nowrap"
                  >
                    <span class="i-ri-calendar-line size-3" />
                    {{ item.createdAt }}
                  </span>
                </div>
              </div>
              <p
                v-if="item.description"
                class="mt-1 ml-6 text-xs text-n-slate-11 line-clamp-1"
              >
                {{ item.description }}
              </p>
            </button>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Seletor de conversas -->
    <Modal
      v-if="showConversationSelector"
      :show="showConversationSelector"
      @close="showConversationSelector = false"
    >
      <div
        class="conversation-selector p-0 bg-transparent border-none shadow-none"
      >
        <div class="px-4 py-3 border-b border-n-weak">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('KANBAN.FORM.NOTES.SELECT_CONVERSATION') }}
          </h4>
        </div>
        <!-- Caixa de busca -->
        <div class="px-4 py-2">
          <input
            v-model="conversationSearchTerm"
            type="text"
            class="w-full px-3 py-2 text-sm border border-n-weak rounded-xl focus:ring-1 focus:ring-woot-500 focus:border-woot-500 bg-n-solid-2"
            placeholder="Buscar..."
          />
        </div>
        <div class="max-h-[280px] overflow-y-auto custom-scrollbar">
          <div
            v-if="conversationsLoadingForModal"
            class="p-4 text-center text-sm text-n-slate-11"
          >
            <span class="loading-spinner w-4 h-4 mr-2" />
            {{ t('KANBAN.LOADING') }}
          </div>
          <div
            v-else-if="filteredConversations.length === 0"
            class="p-4 text-center"
          >
            <p class="text-sm text-n-slate-11">
              {{ t('KANBAN.FORM.NOTES.NO_CONVERSATIONS') }}
            </p>
          </div>
          <div v-else class="divide-y divide-n-weak">
            <button
              v-for="conversation in filteredConversations"
              :key="conversation.id"
              class="w-full px-4 py-3 text-left hover:bg-n-alpha-2 transition-colors focus:outline-none focus:bg-n-alpha-1"
              @click="selectConversation(conversation)"
            >
              <div class="flex items-center gap-3">
                <div
                  class="flex-shrink-0 w-1.5 h-5 rounded-full"
                  :class="{
                    'bg-green-500': conversation.status === 'open',
                    'bg-yellow-500': conversation.status === 'pending',
                    'bg-n-slate-9': conversation.status === 'resolved',
                  }"
                />
                <div class="flex items-center gap-3 flex-1 min-w-0">
                  <h4
                    class="text-sm font-medium text-n-slate-12 truncate flex-1"
                  >
                    #{{ conversation.id }} - {{ conversation.title }}
                  </h4>
                  <span
                    class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11 whitespace-nowrap"
                  >
                    {{ conversation.channel_type }}
                  </span>
                  <span
                    class="flex items-center gap-1 text-xs text-n-slate-10 whitespace-nowrap"
                  >
                    <span class="i-ri-contacts-line size-3" />
                    {{ conversation.created_at }}
                  </span>
                </div>
              </div>
              <p
                v-if="conversation.description"
                class="mt-1 ml-6 text-xs text-n-slate-11 line-clamp-1"
              >
                {{ conversation.description }}
              </p>
            </button>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Seletor de contatos -->
    <Modal
      v-if="showContactSelector"
      :show="showContactSelector"
      @close="showContactSelector = false"
    >
      <div class="contact-selector p-0 bg-transparent border-none shadow-none">
        <div class="px-4 py-3 border-b border-n-weak">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('KANBAN.FORM.NOTES.SELECT_CONTACT') }}
          </h4>
        </div>
        <!-- Caixa de busca -->
        <div class="px-4 py-2">
          <input
            v-model="contactSearchTerm"
            type="text"
            class="w-full px-3 py-2 text-sm border border-n-weak rounded-xl focus:ring-1 focus:ring-woot-500 focus:border-woot-500 bg-n-solid-2"
            placeholder="Buscar..."
          />
        </div>
        <div class="max-h-[280px] overflow-y-auto custom-scrollbar">
          <div
            v-if="contactsLoadingForModal"
            class="p-4 text-center text-sm text-n-slate-11"
          >
            <span class="loading-spinner w-4 h-4 mr-2" />
            {{ t('KANBAN.LOADING') }}
          </div>
          <div
            v-else-if="filteredContactsList.length === 0"
            class="p-4 text-center"
          >
            <p class="text-sm text-n-slate-11">
              {{ t('KANBAN.FORM.NOTES.NO_CONTACTS') }}
            </p>
          </div>
          <div v-else class="divide-y divide-n-weak">
            <button
              v-for="contact in filteredContactsList"
              :key="contact.id"
              class="w-full px-4 py-3 text-left hover:bg-n-alpha-2 transition-colors focus:outline-none focus:bg-n-alpha-1"
              @click="selectContact(contact)"
            >
              <div class="flex items-center gap-3">
                <Avatar
                  v-if="contact.avatar_url"
                  :src="contact.avatar_url"
                  :name="contact.name"
                  :size="24"
                />
                <div class="flex items-center gap-2">
                  <h3 class="text-sm font-medium text-n-slate-12">
                    {{ contact.name }}
                  </h3>
                </div>
              </div>
              <div
                v-if="contact.phone || contact.last_activity_at"
                class="mt-1 ml-9 flex items-center gap-4 text-xs text-n-slate-11"
              >
                <span v-if="contact.phone" class="flex items-center gap-1">
                  <span class="i-ri-phone-line size-3" />
                  {{ contact.phone }}
                </span>
                <span
                  v-if="contact.last_activity_at"
                  class="flex items-center gap-1"
                >
                  <span class="i-ri-alarm-line size-3" />
                  {{ t('KANBAN.FORM.NOTES.LAST_ACTIVITY') }}:
                  {{ contact.last_activity_at }}
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Seção de notas -->
    <div class="notes-section pb-8">
      <!-- Botão de ordenação -->
      <div
        v-if="displayNotes.length > 0"
        class="flex justify-between items-center mb-4"
      >
        <div class="flex items-center gap-2 min-w-0">
          <h3 class="text-lg font-extrabold text-n-slate-12 truncate">
            {{ t('KANBAN.NOTES.TITLE') }}
          </h3>
          <span
            class="inline-flex items-center justify-center shrink-0 min-w-[1.5rem] px-2 py-0.5 text-xs font-semibold tabular-nums rounded-full text-n-slate-12 bg-n-solid-3"
            role="status"
            :aria-label="
              t('KANBAN.NOTES.TOTAL_COUNT_ARIA', { count: displayNotes.length })
            "
          >
            {{ displayNotes.length }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <button
            v-if="hasSelection && displayNotes.length > 0"
            type="button"
            class="sort-button"
            @click="selectAllNotes"
          >
            <span class="i-ri-check-line size-4" />
            <span class="ml-1">{{ t('KANBAN.NOTES.SELECT_ALL') }}</span>
          </button>
          <button
            v-if="hasSelection"
            type="button"
            class="sort-button"
            style="color: #dc2626"
            @click="handleDeleteSelected"
          >
            <span class="i-ri-delete-bin-line size-4" />
            <span class="ml-1">{{ t('KANBAN.NOTES.DELETE_SELECTED') }}</span>
          </button>
          <button
            v-if="displayNotes.length > 1"
            class="sort-button"
            :title="getSortOrderText()"
            @click="toggleSortOrder"
          >
            <span class="i-ri-arrow-up-down-line size-4" />
            <span class="ml-1">{{ getSortOrderText() }}</span>
          </button>
        </div>
      </div>

      <!-- Estado vazio: sem notas -->
      <div
        v-if="!loadingNotes && displayNotes.length === 0"
        class="flex flex-col items-center justify-center py-10 px-4 text-center rounded-xl border border-dashed border-n-weak bg-n-alpha-1"
        role="status"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-10 mb-4 shrink-0"
          aria-hidden="true"
        >
          <path
            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
          />
          <path d="M14 2v6h6" />
          <path d="M16 13H8" />
          <path d="M16 17H8" />
          <path d="M10 9H8" />
        </svg>
        <p class="text-sm font-medium text-n-slate-12 mb-1">
          {{ t('KANBAN.NOTES.EMPTY_TITLE') }}
        </p>
        <p class="text-xs text-n-slate-11 max-w-sm leading-relaxed">
          {{ t('KANBAN.NOTES.EMPTY_SUBTITLE') }}
        </p>
      </div>

      <!-- Lista de notas -->
      <div v-for="note in displayNotes" :key="note.id" class="note-card group">
        <div class="note-layout flex gap-3">
          <!-- Checkbox à esquerda do conteúdo, sempre visível -->
          <div class="flex-shrink-0 w-6 flex items-start pt-0.5">
            <input
              type="checkbox"
              :checked="isNoteSelected(note.id)"
              class="w-4 h-4 rounded border-n-weak text-woot-500 focus:ring-woot-500 cursor-pointer"
              @click="toggleNoteSelection($event, note.id)"
            />
          </div>
          <div class="note-content flex-1 min-w-0">
            <div class="flex items-start justify-between">
              <div class="note-text" v-html="renderMarkdown(note.text)" />
              <!-- Botões de ação -->
              <div class="flex items-center gap-1">
                <button
                  type="button"
                  class="p-1 text-n-slate-10 hover:text-n-slate-11"
                  :title="t('KANBAN.NOTES.SEND_AS_MESSAGE')"
                  :aria-label="t('KANBAN.NOTES.SEND_AS_MESSAGE')"
                  @click="handleSendNoteAsMessage(note)"
                >
                  <span class="icon-button">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="text-current"
                      aria-hidden="true"
                    >
                      <path
                        d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"
                      />
                    </svg>
                  </span>
                </button>
                <button
                  class="p-1 text-n-slate-10 hover:text-n-slate-11"
                  @click="handleEditNote(note)"
                >
                  <span class="icon-button">
                    <span class="i-ri-pencil-line size-4" />
                  </span>
                </button>
                <button
                  class="p-1 text-n-slate-10 hover:text-n-ruby-9 dark:hover:text-n-ruby-4"
                  @click="handleDeleteNote(note.id)"
                >
                  <span class="icon-button">
                    <span class="i-ri-delete-bin-line size-4" />
                  </span>
                </button>
              </div>
            </div>

            <!-- Vinculações da nota -->
            <div
              v-if="
                note.linked_item_id ||
                note.linked_conversation_id ||
                note.linked_contact_id
              "
              class="note-links mt-3 mb-3"
            >
              <!-- Item vinculado -->
              <div v-if="note.linked_item_id" class="linked-item">
                <div :style="linkedItemNoteCardStyle">
                  <span
                    class="inline-flex shrink-0"
                    :style="{ color: linkedItemNoteIconColor }"
                  >
                    <span class="i-ri-link size-3.5" />
                  </span>
                  <span
                    class="text-sm"
                    :style="{ color: linkedItemNoteTextColor }"
                  >
                    {{ t('KANBAN.FORM.NOTES.LINKED_ITEM') }}:
                    {{
                      getLinkedItemDetails(note.linked_item_id)?.title ||
                      `#${note.linked_item_id}`
                    }}
                  </span>
                </div>
              </div>

              <!-- Conversa vinculada -->
              <div
                v-if="note.linked_conversation_id"
                class="linked-conversation mt-2"
              >
                <div
                  class="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800"
                >
                  <span
                    class="i-ri-chat-1-line size-3.5 text-green-600 dark:text-green-400"
                  />
                  <span class="text-sm text-green-700 dark:text-green-300">
                    {{ t('KANBAN.FORM.NOTES.LINKED_CONVERSATION') }}:
                    {{
                      getLinkedConversationLabel(note.linked_conversation_id)
                    }}
                  </span>
                </div>
              </div>

              <!-- Contato vinculado -->
              <div v-if="note.linked_contact_id" class="linked-contact mt-2">
                <div
                  class="flex items-center gap-2 p-2 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800"
                >
                  <span
                    class="i-ri-user-line size-3.5 text-purple-600 dark:text-purple-400"
                  />
                  <span class="text-sm text-purple-700 dark:text-purple-300">
                    {{ t('KANBAN.FORM.NOTES.LINKED_CONTACT') }}:
                    {{
                      resolvedContactsList?.find?.(
                        c => c.id === note.linked_contact_id
                      )?.name || `#${note.linked_contact_id}`
                    }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Metadados da nota -->
            <div class="note-metadata">
              <div class="flex items-center gap-2">
                <Avatar
                  :name="note.author"
                  :src="note.author_avatar"
                  :size="20"
                />
                <span class="note-author">{{ note.author }}</span>
              </div>
              <span class="note-date">
                {{ formatDate(note.created_at || new Date()) }}
              </span>
            </div>

            <!-- Arquivos não-imagem -->
            <div v-if="hasNonImageAttachments(note)" class="note-files">
              <div
                v-for="attachment in getNonImageAttachments(note)"
                :key="attachment.id"
                class="file-attachment"
              >
                <div class="file-info">
                  <span class="file-icon">
                    {{ getFileIcon(attachment) }}
                  </span>
                  <a
                    :href="attachment.url"
                    target="_blank"
                    class="file-name"
                    :title="attachment.filename"
                  >
                    {{ attachment.filename }}
                  </a>
                </div>
                <button
                  class="file-action"
                  @click="removeAttachment(attachment)"
                >
                  <span class="i-ri-delete-bin-line size-3" />
                </button>
              </div>
            </div>
          </div>

          <!-- Preview de imagens -->
          <div v-if="hasImageAttachments(note)" class="note-images">
            <div
              v-for="attachment in getImageAttachments(note)"
              :key="attachment.id"
              class="image-preview"
            >
              <img
                :src="attachment.url"
                :alt="attachment.filename"
                class="preview-image"
                @click="openImagePreview(attachment.url)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <Modal
      v-if="showSendMessageModal && conversationForSendMessage"
      v-model:show="showSendMessageModal"
      :on-close="closeSendMessageModal"
      :show-close-button="true"
      size="medium"
      :close-on-backdrop-click="false"
      class="z-50"
    >
      <div class="settings-modal">
        <header class="settings-header">
          <h3 class="text-lg font-extrabold">
            {{ t('KANBAN.SEND_MESSAGE.TITLE') }}
          </h3>
        </header>
        <div class="settings-content">
          <SendMessageTemplate
            :key="sendMessageModalKey"
            :conversation-id="conversationForSendMessage.display_id"
            :current-stage="item.funnel_stage || ''"
            :contact="sendMessageModalContact"
            :conversation="conversationForSendMessage"
            :item="item"
            :initial-custom-message="initialNoteSendMessageText"
            @close="closeSendMessageModal"
            @send="closeSendMessageModal"
          />
        </div>
      </div>
    </Modal>
  </div>
</template>

<style scoped>
/* Estilos específicos do componente NotesTab */
.note-input-section {
  @apply space-y-2;
}

.action-button {
  @apply flex items-center justify-center w-10 h-10 rounded-xl
         hover:text-n-slate-12
          
         hover:bg-n-alpha-2
         bg-n-solid-3 
         transition-all duration-200;

  &:disabled {
    @apply opacity-50 cursor-not-allowed;
  }
}

.action-button-labeled {
  @apply w-auto min-h-10 h-auto px-3 py-2 gap-2 justify-start;
}

.primary-button {
  @apply px-4 py-2 text-sm font-medium text-white
         bg-woot-500 hover:bg-woot-600
         dark:bg-woot-600 dark:hover:bg-woot-700
         rounded-xl transition-colors;

  &:disabled {
    @apply opacity-50 cursor-not-allowed;
  }
}

.notes-section {
  @apply flex flex-col gap-4;
}

.note-card {
  @apply bg-n-solid-2 rounded-xl p-4 mb-4
         border border-n-weak;
}

.note-layout {
  @apply flex justify-between gap-4;
}

.note-content {
  @apply flex-1 flex flex-col justify-between;
}

.note-text {
  @apply flex-1 text-n-slate-12 whitespace-pre-wrap break-words overflow-hidden;
}

.note-metadata {
  @apply flex items-center justify-between text-sm
         mb-3;
}

.note-author {
  @apply font-medium;
}

.note-date {
  @apply text-xs;
}

.note-files {
  @apply mt-4 space-y-2 border-t border-n-weak pt-4;
}

.file-attachment {
  @apply flex items-center justify-between p-2
         bg-n-alpha-2
         rounded-xl text-sm;
}

.file-info {
  @apply flex items-center gap-1.5 flex-1 min-w-0;
}

.file-icon {
  @apply flex-shrink-0 text-n-slate-10;
}

.file-name {
  @apply text-[10px] hover:text-woot-500 dark:hover:text-woot-400
         max-w-[150px]
         whitespace-nowrap;
}

.file-action {
  @apply p-1.5 text-n-slate-10 hover:text-n-ruby-9
            dark:hover:text-n-ruby-4
         rounded-md hover:bg-n-alpha-2
         transition-colors;
}

.note-images {
  @apply flex-shrink-0 flex flex-col gap-3 w-24;
}

.image-preview {
  @apply rounded-xl overflow-hidden bg-n-alpha-2;
}

.preview-image {
  @apply w-24 h-24 object-cover rounded-xl cursor-pointer
         hover:opacity-90 transition-opacity;
}

/* Quando não há imagens, o conteúdo ocupa todo o espaço */
.note-layout:not(:has(.note-images)) .note-content {
  @apply w-full;
}

/* Ajuste para telas menores */
@media (max-width: 640px) {
  .note-layout {
    @apply flex-col gap-3;
  }

  .note-images {
    @apply w-full flex-row flex-wrap;
  }

  .preview-image {
    @apply w-16 h-16;
  }
}

/* Estilos adicionais para os botões de ação */
.note-actions {
  @apply flex items-center gap-1;
}

.note-action-button {
  @apply p-1 text-n-slate-10 transition-colors;
}

.note-action-button:hover {
  @apply text-n-slate-11;
}

.note-action-button.delete:hover {
  @apply text-n-ruby-9 dark:text-n-ruby-4;
}

.icon-button {
  @apply flex items-center justify-center w-6 h-6 rounded-full
         transition-colors duration-200;
}

.icon-button:hover {
  @apply bg-n-alpha-2;
}

.selected-links-preview {
  @apply border-t border-n-weak pt-3;
}

.selected-link {
  @apply transition-all duration-200;
}

/* Estilos para vinculações de notas */
.note-links {
  @apply space-y-2;
}

.linked-item,
.linked-conversation,
.linked-contact {
  @apply transition-all duration-200;
}

.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: var(--color-scrollbar) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: var(--color-scrollbar);
  border-radius: 3px;
}

.dark .custom-scrollbar {
  scrollbar-color: var(--color-scrollbar-dark) transparent;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: var(--color-scrollbar-dark);
}

/* Dropdown de vinculação */
.link-dropdown {
  @apply z-50;
}

.link-dropdown-item {
  @apply flex items-center gap-3 px-4 py-2 text-sm text-n-slate-12
         hover:bg-n-alpha-2 transition-colors w-full text-left;
}

.link-dropdown-item:hover .fluent-icon {
  @apply opacity-80;
}

.link-dropdown-item:disabled {
  @apply opacity-60 cursor-not-allowed;
}

.link-dropdown-item:disabled:hover {
  @apply bg-transparent dark:bg-transparent;
}

/* Botão de ordenação */
.sort-button {
  @apply flex items-center px-3 py-1.5 text-sm font-medium
         text-n-slate-11
         hover:bg-n-alpha-2
         bg-n-solid-3 
         rounded-xl transition-colors duration-200
         border border-n-weak;
}

.sort-button:hover {
  @apply text-n-slate-12;
}

.settings-modal {
  @apply flex flex-col;
}

.settings-modal .settings-header {
  @apply p-4 border-b border-n-weak;
}

.settings-modal .settings-content {
  @apply p-4 space-y-4;
}
</style>
